import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowRight, Code2, Zap, Globe } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [recentPosts, setRecentPosts] = useState([]);

  useEffect(() => {
    fetchRecentPosts();
  }, []);

  const fetchRecentPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts/published?limit=3`);
      setRecentPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 sm:py-32" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-cyan-500/10" />
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.05) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-in">
            <div className="inline-block mb-4 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
              <span className="font-code text-sm text-emerald-400">// Developer & Tech Writer</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="hero-title">
              Building the Future,
              <br />
              <span className="gradient-text text-3xl sm:text-5xl lg:text-6xl">Demystifying Web <br /> Development</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto mb-8" data-testid="hero-subtitle">
              Full-stack developer crafting elegant solutions to complex problems.
              Sharing insights, tutorials, and deep dives into modern web development.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/blog" data-testid="hero-blog-btn">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold px-8 py-6 text-lg">
                  Read Blog <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/about" data-testid="hero-about-btn">
                <Button variant="outline" className="border-zinc-700 hover:border-emerald-500 hover:text-emerald-400 px-8 py-6 text-lg">
                  About Me
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-zinc-950/50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">What I Do</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="glass hover-lift p-8 border-zinc-800" data-testid="feature-card-1">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center mb-4">
                <Code2 className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-xl text-white font-semibold mb-3">Full-Stack Development</h3>
              <p className="text-zinc-400">
                Building scalable web applications with modern technologies.
                React, Node.js, Python, and more.
              </p>
            </Card>

            <Card className="glass hover-lift p-8 border-zinc-800" data-testid="feature-card-2">
              <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl text-white font-semibold mb-3">Performance Optimization</h3>
              <p className="text-zinc-400">
                Making applications faster and more efficient through careful
                analysis and optimization.
              </p>
            </Card>

            <Card className="glass hover-lift p-8 border-zinc-800" data-testid="feature-card-3">
              <div className="w-12 h-12 bg-violet-500/10 rounded-lg flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-violet-400" />
              </div>
              <h3 className="text-xl text-white font-semibold mb-3">Technical Writing</h3>
              <p className="text-zinc-400">
                Sharing knowledge through detailed tutorials, guides, and
                technical deep dives.
              </p>
            </Card>
          </div>
        </div>
      </section>


      {/* Portfolio Section */}
        <section className="py-20" data-testid="recent-posts-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <h2 className="text-3xl font-bold">Recent Projects</h2>
              </div>
            </div>
        </section>

      {/* Recent Posts Section */}
      {recentPosts.length > 0 && (
        <section className="py-20" data-testid="recent-posts-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <h2 className="text-3xl font-bold">Recent Posts</h2>
              <Link to="/blog" data-testid="view-all-posts">
                <Button variant="ghost" className="text-emerald-400 hover:text-emerald-300">
                  View All <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {recentPosts.map((post, index) => (
                <Link key={post.id} to={`/blog/${post.slug}`} data-testid={`recent-post-${index}`}>
                  <Card className="glass hover-lift border-zinc-800 overflow-hidden h-full">
                    {post.featured_image && (
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-6">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags.slice(0, 2).map((tag, i) => (
                          <span
                            key={i}
                            className="text-xs font-code px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-xl text-white font-semibold mb-2 line-clamp-2">{post.title}</h3>
                      <p className="text-zinc-400 text-sm line-clamp-3">{post.excerpt}</p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-zinc-950/50" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Let's Connect</h2>
          <p className="text-zinc-400 text-lg mb-8">
            Interested in collaborating or just want to say hi? Feel free to reach out.
          </p>
          <Link to="/contact" data-testid="cta-contact-btn">
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold px-8 py-6 text-lg">
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
